import React, { useMemo, useState } from 'react';
import Modal from '../../../../Components/Modal/Modal';
import { useTreatmentContext } from '../../../../Services/TreatmentContext';
import { Size } from '../../../../Enums/Size.enum';
import ElectronicPrescriptionDetailWrapper from '../../../Prescription/Components/ElectronicPrescriptionDetail/ElectronicPrescriptionDetailWrapper';
import apiClient from '../../../../Services/ApiClient';
import { useHealthPlanContext } from '../../../../Services/HealthPlanContext';
import useNotification from '../../../../Hooks/useNotification.hook';
import HealthPlanPrintPdfModal from '../HealthPlanPrintPdf/HealthPlanPrintPdfModal';
import { v4 as uuidv4 } from 'uuid';

interface ElectronicPrescriptionLatamDefaultMedicament {
	amount: string;
	period: string;
	medicine: string;
	periodPer: string;
	observations: string;
	presentation: string;
	concentration: string;
	doseFrequency: string;
	medicamentType: string;
	medicationCode: string;
	doseFrequencyPer: string;
	referredDiagnosisId: string | null;
	routeOfAdministration: string;
}

interface ElectronicPrescriptionLatamDefaultValues {
	medicaments: ElectronicPrescriptionLatamDefaultMedicament[];
	expirationDate: string;
	addToPatientECard: boolean;
	alergiesToMedicamentsOrComponents: string;
}

interface HealthPlanElectronicPrescriptionModalProps {
	opened: boolean;
	onClose: any;
	patientId: string;
	disableSubmit?: boolean;
	defaultValues?: ElectronicPrescriptionLatamDefaultValues;
}

const HealthPlanElectronicPrescriptionModal: React.FC<
	HealthPlanElectronicPrescriptionModalProps
> = ({ opened, onClose, patientId, disableSubmit, defaultValues }) => {
	const { isCase } = useTreatmentContext();
	const [printModalOpen, setPrintModalOpen] = useState(false);
	const { healthPlanUser, activeHealthPlanUserItem } = useHealthPlanContext();
	const { showNotificationAlert } = useNotification();

	const preparedDefaultValues = useMemo(() => {
		if (!defaultValues) return undefined;
		return {
			...defaultValues,
			items: defaultValues.medicaments?.map((medicament: any) => ({
				extra: {
					...medicament,
					id: uuidv4(),
				},
			})),
		};
	}, [defaultValues]);

	const handleSubmit = async (values: any) => {
		try {
			if (healthPlanUser && activeHealthPlanUserItem) {
				const result = await apiClient.patch(
					'/health-plan/' +
						healthPlanUser?.healthPlanId +
						'/user/' +
						healthPlanUser?.id +
						'/item/' +
						activeHealthPlanUserItem?.id,
					{
						extra: {
							data: values,
						},
					},
				);

				if (result) {
					setPrintModalOpen(true);
					return result;
				}
			}
		} catch (e: any) {
			if (e.response.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		} finally {
			onClose?.();
		}
	};

	const handleAfterPrint = async () => {
		setPrintModalOpen(false);
		onClose?.();
	};

	return (
		<>
			<Modal opened={opened} onClose={onClose} wrapChat={isCase} size={Size.XL}>
				<Modal.Header title={'base.electronicPrescription'} />
				<Modal.Content contentScroll={true}>
					<ElectronicPrescriptionDetailWrapper
						onSubmit={handleSubmit}
						onClose={onClose}
						patientId={patientId}
						disableSubmit={disableSubmit}
						defaultValues={preparedDefaultValues}
					/>
				</Modal.Content>
			</Modal>
			<HealthPlanPrintPdfModal
				opened={printModalOpen}
				onClose={handleAfterPrint}
				healthPlanUser={healthPlanUser}
				healthPlanUserItem={activeHealthPlanUserItem}
			/>
		</>
	);
};

export default HealthPlanElectronicPrescriptionModal;
